import { boundMethod } from 'autobind-decorator';
import {
  AsyncStatus,
  Response,
  Template,
  TemplatesCreate,
  TemplatesUpdate,
  TemplateTranslations,
} from 'lib-shared-legacy-types';
import isEqual from 'lodash/isEqual';
import memoize from 'memoize-one';
import { ComponentType, forwardRef, PureComponent, Ref } from 'react';

import { ajaxSend } from 'api/api';

import {
  createTemplate,
  getTemplates,
  readTemplateById,
  removeTemplate,
  removeTemplateTranslations,
  updateTemplate,
  updateTemplateTranslations,
} from './api/templates';
import {
  TemplatesGetItemsResponse,
  TemplatesQueryParams,
  TemplatesResponse,
} from './api/types';

const DEFAULT_FETCH_THROTTLE = 300;

export interface WithTemplatesInjectedProps<TNormalizedTemplate = Template> {
  templates: {
    /** Loaded templates of current page */
    items: Array<TNormalizedTemplate>;

    /** AsyncStatus of load templates api */
    getItemsAsyncStatus: AsyncStatus;

    /** AsyncStatus of create template api */
    createAsyncStatus: AsyncStatus;

    /** AsyncStatus of update template api */
    updateAsyncStatus: AsyncStatus;

    /** AsyncStatus of get template api */
    getAsyncStatus: AsyncStatus;

    /** AsyncStatus of remove template api */
    removeAsyncStatus: AsyncStatus;

    /** AsyncStatus of update template translations api */
    updateTranslationsAsyncStatus: AsyncStatus;

    /** AsyncStatus of remove template translations api */
    removeTranslationsAsyncStatus: AsyncStatus;

    /** The params used in get templates api */
    params: TemplatesQueryParams;

    /** Whether there is next page or not */
    hasNextPage: boolean;

    /** Whether there is previous page or not */
    hasPreviousPage: boolean;

    /**
     * Action to get templates. It will use the current params to filter
     * templates.
     */
    getItems: () => Promise<
      Response<{ templates: Array<TNormalizedTemplate> }>
    >;

    /**
     * Handle params change.
     *
     * @param {TemplatesQueryParams} params - The params
     * @param {number} [fetchThrottle=300] - If > 0 it will re-fetch templates
     *   after given milliseconds. Default is `300`
     */
    paramsChange: (
      params: TemplatesQueryParams,
      fetchThrottle?: number,
    ) => Promise<Response<{ templates: Array<TNormalizedTemplate> }> | void>;

    /** Get templates of the next page */
    nextPage: () => Promise<
      Response<{ templates: Array<TNormalizedTemplate> }>
    >;

    /** Get templates of the previous page */
    previousPage: () => Promise<
      Response<{ templates: Array<TNormalizedTemplate> }>
    >;

    /**
     * Create a new template
     *
     * @param {Template} - The template to be created
     */
    create: (template: TemplatesCreate) => Promise<TemplatesResponse>;

    /**
     * Update a template
     *
     * @param {string} templateId - Id of the template to be updated.
     * @param {TemplatesUpdate} payload - Updated template data
     */
    update: (
      templateId: string,
      payload: TemplatesUpdate,
    ) => Promise<TemplatesResponse>;

    /**
     * Get a template by its id
     *
     * @param {string} templateId - Id of the template to be fetched.
     */
    readTemplateById: (
      templateId: string,
    ) => Promise<TemplatesResponse | undefined>;

    /**
     * Remove a template
     *
     * @param {string} templateId - Id of the template to be removed.
     */
    remove: (templateId: string) => Promise<TemplatesResponse>;

    /**
     * Update a template translations
     *
     * @param {string} templateId - Id of the template to be updated.
     * @param {TemplateTranslations} payload - Updated template translations
     *   data
     */
    updateTranslations: (
      templateId: string,
      payload: TemplateTranslations,
    ) => Promise<unknown>;

    /**
     * Remove translations from a template
     *
     * @param {string[]} locales - Locale of the translations to be removed.
     */
    removeTranslations: (
      templateId: string,
      locales: string[],
    ) => Promise<unknown>;
  };
}

type WrappedComponentOwnProps<T, TNormalizedTemplate> = Omit<
  T,
  keyof WithTemplatesInjectedProps<TNormalizedTemplate>
>;

/** The position of the new data, used when `settings.infinite` is `true` */
enum NewDataPosition {
  PreviousPage = -1,
  Replace = 0,
  NextPage = 1,
}

interface WithTemplatesState<TNormalizedTemplate> {
  params: TemplatesQueryParams;
  items: Array<TNormalizedTemplate>;
  nextPagePath: string | null;
  previousPagePath: string | null;
  getItemsAsyncStatus: AsyncStatus;
  createAsyncStatus: AsyncStatus;
  updateAsyncStatus: AsyncStatus;
  readTemplateByIdAsyncStatus: AsyncStatus;
  removeAsyncStatus: AsyncStatus;
  updateTranslationsAsyncStatus: AsyncStatus;
  removeTranslationsAsyncStatus: AsyncStatus;
}

interface WithTemplatesSettings<TNormalizedTemplate> {
  initialParams?: TemplatesQueryParams;
  infinite?: boolean;
  itemNormalizer?: (
    /** Current item (iteratee) */
    item: Template,
    /** Current params from HOC state */
    params: TemplatesQueryParams,
    /** Item index */
    index: number,
  ) => TNormalizedTemplate;
}

export const withTemplates = <
  T extends WithTemplatesInjectedProps<TNormalizedTemplate>,
  TNormalizedTemplate = Template,
>(
  WrappedComponent: ComponentType<T>,
  settings: WithTemplatesSettings<TNormalizedTemplate> = { infinite: false },
) => {
  type WrappedComponentInstance = InstanceType<any>;

  type WithTemplatesProps<T> = WrappedComponentOwnProps<
    T,
    TNormalizedTemplate
  > & {
    forwardedRef: Ref<WrappedComponentInstance>;
  };

  type NormalizedState = WithTemplatesState<TNormalizedTemplate>;

  type NormalizedProps = WithTemplatesInjectedProps<TNormalizedTemplate>;

  type NormalizedResponse = Response<{ templates: Array<TNormalizedTemplate> }>;

  class WithTemplates extends PureComponent<
    WithTemplatesProps<T>,
    WithTemplatesState<TNormalizedTemplate>
  > {
    timeout: ReturnType<typeof setTimeout> | null;

    constructor(props: WithTemplatesProps<T>) {
      super(props);

      this.timeout = null;
      this.state = {
        items: [],
        params: { limit: 25, ...settings.initialParams },
        nextPagePath: null,
        previousPagePath: null,
        getItemsAsyncStatus: 'succeeded',
        createAsyncStatus: 'succeeded',
        updateAsyncStatus: 'succeeded',
        readTemplateByIdAsyncStatus: 'succeeded',
        removeAsyncStatus: 'succeeded',
        updateTranslationsAsyncStatus: 'succeeded',
        removeTranslationsAsyncStatus: 'succeeded',
      };
      this.memoizedGetInjectedProps = memoize(
        this.memoizedGetInjectedProps,
        isEqual,
      ).bind(this); // We have to bind it like this. Using @boundMethod won't work
    }

    componentWillUnmount() {
      if (this.timeout) {
        clearTimeout(this.timeout);
      }
    }

    @boundMethod
    getItems(
      position: NewDataPosition = NewDataPosition.Replace,
    ): Promise<NormalizedResponse> {
      this.setState({ getItemsAsyncStatus: 'loading' });

      const params = { ...this.state.params };

      if (params?.search) {
        delete params.sort_by; // We do not support sort and search in the same query
      }

      return getTemplates(params)
        .then((resp) => this.handleQueryResolve(resp, position))
        .catch(this.handleQueryReject);
    }

    @boundMethod
    getItemsWithPath(
      path: string,
      position: NewDataPosition,
    ): Promise<NormalizedResponse> {
      this.setState({ getItemsAsyncStatus: 'loading' });

      return ajaxSend<TemplatesGetItemsResponse>({ path })
        .then((resp) => this.handleQueryResolve(resp, position))
        .catch(this.handleQueryReject);
    }

    @boundMethod
    nextPage(): Promise<NormalizedResponse> | never {
      const { nextPagePath } = this.state;
      if (nextPagePath) {
        return this.getItemsWithPath(nextPagePath, NewDataPosition.NextPage);
      }
      throw new Error('Next page does not exist');
    }

    @boundMethod
    previousPage(): Promise<NormalizedResponse> | never {
      const { previousPagePath } = this.state;
      if (previousPagePath) {
        return this.getItemsWithPath(
          previousPagePath,
          NewDataPosition.PreviousPage,
        );
      }
      throw new Error('Previous page does not exist');
    }

    @boundMethod
    create(template: TemplatesCreate): Promise<TemplatesResponse> {
      this.setState({ createAsyncStatus: 'loading' });
      return createTemplate(template)
        .then((response) => {
          this.setState({ createAsyncStatus: 'succeeded' });
          return response;
        })
        .catch((err) => {
          this.setState({ createAsyncStatus: 'failed' });
          throw err;
        });
    }

    @boundMethod
    update(
      templateId: string,
      payload: TemplatesUpdate,
    ): Promise<TemplatesResponse> {
      this.setState({ updateAsyncStatus: 'loading' });
      return updateTemplate(templateId, payload)
        .then((response) => {
          this.setState({ updateAsyncStatus: 'succeeded' });
          return response;
        })
        .catch((err) => {
          this.setState({ updateAsyncStatus: 'failed' });
          throw err;
        });
    }

    @boundMethod
    readTemplateById(
      templateId: string,
    ): Promise<TemplatesResponse | undefined> {
      this.setState({ readTemplateByIdAsyncStatus: 'loading' });
      return readTemplateById(templateId)
        .then((response) => {
          this.setState({
            readTemplateByIdAsyncStatus: 'succeeded',
            items: this.state.items.concat(
              this.getNormalizedItems(response ? [response.data] : []),
            ),
          });
          return response;
        })
        .catch((err) => {
          this.setState({ readTemplateByIdAsyncStatus: 'failed' });
          throw err;
        });
    }

    @boundMethod
    remove(templateId: string): Promise<TemplatesResponse> {
      this.setState({ removeAsyncStatus: 'loading' });
      return removeTemplate(templateId)
        .then((response) => {
          this.setState({ removeAsyncStatus: 'succeeded' });
          return response;
        })
        .catch((err) => {
          this.setState({ removeAsyncStatus: 'failed' });
          throw err;
        });
    }

    @boundMethod
    updateTranslations(
      templateId: string,
      payload: TemplateTranslations,
    ): Promise<TemplatesResponse> {
      this.setState({ updateTranslationsAsyncStatus: 'loading' });
      return updateTemplateTranslations(templateId, payload)
        .then((response) => {
          this.setState({ updateTranslationsAsyncStatus: 'succeeded' });
          return response;
        })
        .catch((err) => {
          this.setState({ updateTranslationsAsyncStatus: 'failed' });
          throw err;
        });
    }

    @boundMethod
    removeTranslations(
      templateId: string,
      locales: string[],
    ): Promise<TemplatesResponse> {
      this.setState({ removeTranslationsAsyncStatus: 'loading' });
      return removeTemplateTranslations(templateId, locales)
        .then((response) => {
          this.setState({ removeTranslationsAsyncStatus: 'succeeded' });
          return response;
        })
        .catch((err) => {
          this.setState({ removeTranslationsAsyncStatus: 'failed' });
          throw err;
        });
    }

    @boundMethod
    handleParamsChange(
      params?: TemplatesQueryParams,
      fetchThrottle: number = DEFAULT_FETCH_THROTTLE,
    ) {
      return new Promise<NormalizedResponse | void>((resolve, reject) => {
        this.setState(
          (state) => ({ params: { ...state.params, ...params } }),
          () => {
            if (fetchThrottle >= 0) {
              if (this.timeout) {
                clearTimeout(this.timeout);
              }

              this.timeout = setTimeout(() => {
                resolve(this.getItems());
              }, fetchThrottle);
            } else {
              resolve();
            }
          },
        );
      });
    }

    getPaginatedItems(
      newItems: Array<TNormalizedTemplate>,
      oldItems: Array<TNormalizedTemplate>,
      position: NewDataPosition = NewDataPosition.Replace,
    ): Array<TNormalizedTemplate> {
      if (!settings.infinite || position === NewDataPosition.Replace) {
        return newItems;
      }

      if (position === NewDataPosition.NextPage) {
        return oldItems.concat(newItems);
      }

      if (position === NewDataPosition.PreviousPage) {
        return newItems.concat(oldItems);
      }

      return [];
    }

    getNormalizedItems(items: Array<Template>): Array<TNormalizedTemplate> {
      if (settings.itemNormalizer === undefined) {
        // In this case we are sure that NormalizedTemplate is actually Template
        // There might be some complicated ways to avoid this casting, but it doesn't really worth it
        return items as unknown as Array<TNormalizedTemplate>;
      }

      // we have to cast this to make typescript silent.
      // we know that `settings.itemNormalizer` is not `undefined`, because we checked it before,
      // but typescript doesn't understand that, because we are calling it inside map function!
      const itemNormalizer = settings.itemNormalizer as any;
      return items.map((item, index) =>
        itemNormalizer(item, this.state.params, index),
      );
    }

    @boundMethod
    handleQueryResolve(
      response: TemplatesGetItemsResponse,
      position: NewDataPosition = NewDataPosition.Replace,
    ): Promise<NormalizedResponse> {
      return new Promise((resolve) => {
        this.setState(
          (state) => ({
            getItemsAsyncStatus: 'succeeded',
            items: this.getPaginatedItems(
              this.getNormalizedItems(response.data.templates),
              state.items,
              position,
            ),
            nextPagePath: response.meta.next_page,
            previousPagePath: response.meta.prev_page,
          }),
          () =>
            resolve({
              ...response,
              data: {
                ...response.data,
                templates: this.state.items,
              },
            }),
        );
      });
    }

    @boundMethod
    handleQueryReject(error) {
      this.setState({
        getItemsAsyncStatus: 'failed',
      });

      return error;
    }

    @boundMethod
    getInjectedProps(
      props: Readonly<WithTemplatesProps<T>>,
      state: Readonly<WithTemplatesState<TNormalizedTemplate>>,
    ): NormalizedProps['templates'] {
      const {
        items,
        params,
        nextPagePath,
        previousPagePath,
        getItemsAsyncStatus,
        createAsyncStatus,
        updateAsyncStatus,
        readTemplateByIdAsyncStatus,
        removeAsyncStatus,
        updateTranslationsAsyncStatus,
        removeTranslationsAsyncStatus,
      } = state;

      return this.memoizedGetInjectedProps(
        params,
        items,
        getItemsAsyncStatus,
        createAsyncStatus,
        updateAsyncStatus,
        readTemplateByIdAsyncStatus,
        removeAsyncStatus,
        updateTranslationsAsyncStatus,
        removeTranslationsAsyncStatus,
        nextPagePath,
        previousPagePath,
      );
    }

    memoizedGetInjectedProps(
      params: Readonly<NormalizedState>['params'],
      items: Readonly<NormalizedState>['items'],
      getItemsAsyncStatus: Readonly<NormalizedState>['getItemsAsyncStatus'],
      createAsyncStatus: Readonly<NormalizedState>['createAsyncStatus'],
      updateAsyncStatus: Readonly<NormalizedState>['updateAsyncStatus'],
      getAsyncStatus: Readonly<NormalizedState>['readTemplateByIdAsyncStatus'],
      removeAsyncStatus: Readonly<NormalizedState>['removeAsyncStatus'],
      updateTranslationsAsyncStatus: Readonly<NormalizedState>['updateTranslationsAsyncStatus'],
      removeTranslationsAsyncStatus: Readonly<NormalizedState>['removeTranslationsAsyncStatus'],
      nextPagePath: Readonly<NormalizedState>['nextPagePath'],
      previousPagePath: Readonly<NormalizedState>['previousPagePath'],
    ): NormalizedProps['templates'] {
      return {
        params: params,
        items: items,
        getItemsAsyncStatus: getItemsAsyncStatus,
        createAsyncStatus: createAsyncStatus,
        updateAsyncStatus: updateAsyncStatus,
        getAsyncStatus: getAsyncStatus,
        removeAsyncStatus: removeAsyncStatus,
        updateTranslationsAsyncStatus: updateTranslationsAsyncStatus,
        removeTranslationsAsyncStatus: removeTranslationsAsyncStatus,
        hasNextPage: nextPagePath !== null,
        hasPreviousPage: previousPagePath !== null,
        getItems: this.getItems,
        paramsChange: this.handleParamsChange,
        nextPage: this.nextPage,
        previousPage: this.previousPage,
        create: this.create,
        update: this.update,
        readTemplateById: this.readTemplateById,
        remove: this.remove,
        updateTranslations: this.updateTranslations,
        removeTranslations: this.removeTranslations,
      };
    }

    render() {
      const { forwardedRef, ...props } = this.props;

      return (
        <WrappedComponent
          {...(props as unknown as T)}
          templates={this.getInjectedProps(this.props, this.state)}
          ref={forwardedRef}
        />
      );
    }
  }

  // eslint-disable-next-line react/display-name
  return forwardRef<
    WrappedComponentInstance,
    WrappedComponentOwnProps<T, TNormalizedTemplate>
  >((props, ref) => <WithTemplates {...(props as any)} forwardedRef={ref} />);
};
